import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import {
    browserSessionPersistence,
    createUserWithEmailAndPassword,
    onAuthStateChanged,
    sendEmailVerification,
    setPersistence,
    signInWithEmailAndPassword,
    signOut,
    updateProfile,
    User,
} from 'firebase/auth';
import { Link, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import {
    BillingInterval,
    createSelfServeSignup,
    createSubscriptionCheckoutSession,
    getSelfServeSignupEligibility,
    getSaasErrorMessage,
    SelfServeSignupEligibility,
    SelfServicePlanCode,
} from './SaasApi';
import { PublicError, PublicLayout, PublicLoading } from './PublicLayout';
import { prepareProvisionedDashboardSession, reloadDashboard } from './DashboardNavigation';
import { getPremiumTotalPrice } from './premiumCapacity';
import { clearSignupDraft, createSignupDraftId, loadSignupDraft, saveSignupDraft } from './SignupDraft';
import { usePublicPlanCatalog } from './usePublicPlanCatalog';

const validPlanCodes: SelfServicePlanCode[] = ['starter', 'premium'];
const MAX_SELF_SERVICE_UNITS = 20;
const gbpFormatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

// Accept only whole self-service quantities; the catalogue performs the plan-specific check below.
const parseSubscriptionUnits = (value: string | null): number | null => {
    if (!value || !/^\d+$/.test(value)) return null;
    const units = Number(value);
    return Number.isInteger(units) && units >= 1 && units <= MAX_SELF_SERVICE_UNITS ? units : null;
};

type ClinicNameCheck = {
    name: string;
    status: 'idle' | 'checking' | 'available' | 'taken' | 'error';
    message?: string;
};

const isSecureCheckoutUrl = (value: string): boolean => {
    try {
        return new URL(value).protocol === 'https:';
    } catch {
        return false;
    }
};

export const SignupPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const { catalog, isLoading: isCatalogLoading, error: catalogError, reload } = usePublicPlanCatalog();
    const planCode = searchParams.get('plan') as SelfServicePlanCode | null;
    const versionId = searchParams.get('version');
    const intervalValue = searchParams.get('interval');
    const interval: BillingInterval | null = intervalValue === 'monthly' || intervalValue === 'annual'
        ? intervalValue
        : null;
    const selectedUnits = parseSubscriptionUnits(searchParams.get('units'));
    const shouldResumeSignup = searchParams.get('resume') === '1';
    const resumeDraftId = searchParams.get('draft');
    const initialDraft = useMemo(
        () => loadSignupDraft(planCode, versionId, interval, resumeDraftId, selectedUnits),
        [interval, planCode, resumeDraftId, selectedUnits, versionId]
    );
    const subscriptionUnits = initialDraft?.subscriptionUnits || selectedUnits ||
        (planCode === 'starter' ? 1 : null);
    const [draftId, setDraftId] = useState(() => initialDraft?.draftId || createSignupDraftId());
    const [authUser, setAuthUser] = useState<User | null>(auth.currentUser);
    const [isEmailVerified, setIsEmailVerified] = useState(auth.currentUser?.emailVerified === true);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [email, setEmail] = useState(initialDraft?.email || '');
    const [password, setPassword] = useState('');
    const [ownerName, setOwnerName] = useState(auth.currentUser?.displayName || initialDraft?.ownerName || '');
    const [providerName, setProviderName] = useState(initialDraft?.providerName || '');
    const [accountMode, setAccountMode] = useState<'create' | 'sign_in'>(
        initialDraft?.accountCreated || shouldResumeSignup ? 'sign_in' : 'create'
    );
    const [hasPendingAccount, setHasPendingAccount] = useState(
        initialDraft?.accountCreated === true || shouldResumeSignup
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [eligibility, setEligibility] = useState<SelfServeSignupEligibility | null>(null);
    const [eligibilityError, setEligibilityError] = useState<string | null>(null);
    const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);
    const [eligibilityRefresh, setEligibilityRefresh] = useState(0);
    const [clinicNameCheck, setClinicNameCheck] = useState<ClinicNameCheck>({ name: '', status: 'idle' });
    const clinicNameRequest = useRef(0);
    const pendingAccount = useRef(hasPendingAccount);
    const pendingAccountEmail = useRef(initialDraft?.accountCreated ? initialDraft.email.trim().toLowerCase() : '');

    const selectedPlan = useMemo(() => catalog?.plans.find((plan) =>
        plan.planCode === planCode && plan.versionId === versionId
    ), [catalog, planCode, versionId]);

    useEffect(() => onAuthStateChanged(auth, (user) => {
        const observedEmail = (user?.email || '').trim().toLowerCase();
        if (user && pendingAccountEmail.current && pendingAccountEmail.current !== observedEmail) {
            // Never expose one local clinic draft after Firebase restores or signs
            // in a different account in this browser tab.
            void signOut(auth);
            setAuthUser(null);
            setIsEmailVerified(false);
            setIsAuthReady(true);
            setError('This clinic signup belongs to a different owner account. Sign in with the account that received the verification email.');
            return;
        }
        setAuthUser(user);
        setIsEmailVerified(user?.emailVerified === true);
        setIsAuthReady(true);
        if (user?.displayName) {
            setOwnerName((current) => current || user.displayName || '');
        }
        if (!user && pendingAccount.current) {
            setAccountMode('sign_in');
            setPassword('');
            setNotice(shouldResumeSignup
                ? null
                : 'Your account was created. Sign in again to continue verifying your email and finish setting up your clinic.');
        }
    }), [shouldResumeSignup]);

    useEffect(() => {
        pendingAccount.current = hasPendingAccount;
    }, [hasPendingAccount]);

    useEffect(() => {
        if (!planCode || !versionId || !interval || !subscriptionUnits) return;
        saveSignupDraft({
            draftId,
            planCode,
            versionId,
            interval,
            subscriptionUnits,
            email: hasPendingAccount && pendingAccountEmail.current ? pendingAccountEmail.current : email,
            ownerName,
            providerName,
            accountCreated: hasPendingAccount,
        });
    }, [draftId, email, hasPendingAccount, interval, ownerName, planCode, providerName, subscriptionUnits, versionId]);

    // Gate clinic creation on a server-side identity check so existing dashboard users never enter signup.
    useEffect(() => {
        if (!authUser || !isEmailVerified) {
            setEligibility(null);
            setEligibilityError(null);
            setIsCheckingEligibility(false);
            return;
        }
        let cancelled = false;
        setEligibility(null);
        setEligibilityError(null);
        setIsCheckingEligibility(true);
        getSelfServeSignupEligibility()
            .then((result) => {
                if (cancelled) return;
                setEligibility(result);
                setNotice(null);
            })
            .catch((eligibilityFailure: unknown) => {
                if (cancelled) return;
                setEligibilityError(getSaasErrorMessage(
                    eligibilityFailure,
                    'We could not check this account. Please try again.'
                ));
            })
            .finally(() => {
                if (!cancelled) setIsCheckingEligibility(false);
            });
        return () => { cancelled = true; };
    }, [authUser, isEmailVerified, eligibilityRefresh]);

    // Resume mode also survives Firebase's hosted action page, which may open in a separate browser tab.
    const verificationActionSettings = () => {
        const returnUrl = new URL('/signup', window.location.origin);
        if (planCode && versionId && interval && subscriptionUnits) {
            returnUrl.search = new URLSearchParams({
                plan: planCode,
                version: versionId,
                interval,
                units: String(subscriptionUnits),
                resume: '1',
                draft: draftId,
            }).toString();
        }
        return { url: returnUrl.toString(), handleCodeInApp: false };
    };

    const createAccount = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);
        setNotice(null);
        if (!ownerName.trim() || !providerName.trim()) {
            setError('Enter your name and clinic name.');
            return;
        }
        if (password.length < 8) {
            setError('Choose a password with at least 8 characters.');
            return;
        }

        setIsSubmitting(true);
        try {
            await setPersistence(auth, browserSessionPersistence);
            const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
            pendingAccount.current = true;
            pendingAccountEmail.current = (credential.user.email || email).trim().toLowerCase();
            setHasPendingAccount(true);
            setPassword('');
            // Persist the non-sensitive clinic draft before sending a link that may
            // be opened in a different browser tab, and before profile updates can fail.
            // Passwords are never stored.
            if (planCode && versionId && interval && subscriptionUnits) {
                saveSignupDraft({
                    draftId,
                    planCode,
                    versionId,
                    interval,
                    subscriptionUnits,
                    email: credential.user.email || email.trim(),
                    ownerName: ownerName.trim(),
                    providerName: providerName.trim(),
                    accountCreated: true,
                });
            }
            await updateProfile(credential.user, { displayName: ownerName.trim() });
            await sendEmailVerification(credential.user, verificationActionSettings());
            setAuthUser(credential.user);
            setIsEmailVerified(false);
            setNotice(`We sent a verification link to ${credential.user.email}. Open it, then return here.`);
        } catch (accountError: unknown) {
            if ((accountError as { code?: string })?.code === 'auth/email-already-in-use') {
                setAccountMode('sign_in');
                setError('An account already exists for this email. Sign in instead to continue with the selected plan.');
            } else {
                setError(getSaasErrorMessage(accountError));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const signInOwnerAccount = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);
        setNotice(null);
        setIsSubmitting(true);
        try {
            await setPersistence(auth, browserSessionPersistence);
            const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
            const signedInEmail = (credential.user.email || '').trim().toLowerCase();
            if (pendingAccountEmail.current && pendingAccountEmail.current !== signedInEmail) {
                await signOut(auth);
                setAuthUser(null);
                setIsEmailVerified(false);
                setError('This clinic signup belongs to a different owner account. Sign in with the account that received the verification email.');
                return;
            }
            pendingAccount.current = true;
            pendingAccountEmail.current = signedInEmail;
            setHasPendingAccount(true);
            setPassword('');
            setAuthUser(credential.user);
            setIsEmailVerified(credential.user.emailVerified);
            setNotice(credential.user.emailVerified
                ? null
                : `Verify ${credential.user.email} before creating your clinic.`);
        } catch (signInError: unknown) {
            setError(getSaasErrorMessage(signInError));
        } finally {
            setIsSubmitting(false);
        }
    };

    const refreshVerification = async () => {
        if (!authUser) return;
        setError(null);
        setIsSubmitting(true);
        try {
            const currentUser = auth.currentUser;
            if (!currentUser || currentUser.uid !== authUser.uid) {
                setAuthUser(null);
                setIsEmailVerified(false);
                setAccountMode('sign_in');
                setNotice('Your email may already be verified. Sign in again to continue with this plan.');
                return;
            }
            await currentUser.reload();
            const refreshedUser = auth.currentUser;
            setAuthUser(refreshedUser);
            setIsEmailVerified(refreshedUser?.emailVerified === true);
            if (!refreshedUser?.emailVerified) {
                setNotice('Your email is not verified yet. Open the link in your email, then try again.');
                return;
            }
            await refreshedUser.getIdToken(true);
            setNotice('Email verified. You can now create your clinic.');
        } catch (verificationError: unknown) {
            setError(getSaasErrorMessage(verificationError));
        } finally {
            setIsSubmitting(false);
        }
    };

    const resendVerification = async () => {
        if (!authUser) return;
        setError(null);
        setIsSubmitting(true);
        try {
            await sendEmailVerification(authUser, verificationActionSettings());
            setNotice(`A new verification link was sent to ${authUser.email}.`);
        } catch (verificationError: unknown) {
            setError(getSaasErrorMessage(verificationError));
        } finally {
            setIsSubmitting(false);
        }
    };

    const createClinic = async (event: FormEvent) => {
        event.preventDefault();
        if (!selectedPlan || !interval || !subscriptionUnits || !authUser || !isEmailVerified ||
            eligibility?.accountStatus !== 'eligible') return;
        if (!ownerName.trim() || !providerName.trim()) {
            setError('Enter your name and clinic name.');
            return;
        }

        setError(null);
        setIsSubmitting(true);
        try {
            const checkedName = providerName.trim();
            if (clinicNameCheck.name !== checkedName || clinicNameCheck.status !== 'available') {
                const isAvailable = await checkClinicNameAvailability(checkedName);
                if (!isAvailable) {
                    setIsSubmitting(false);
                    return;
                }
            }
            const result = await createSelfServeSignup({
                planCode: selectedPlan.planCode,
                versionId: selectedPlan.versionId,
                billingInterval: interval,
                subscriptionUnits,
                providerName: providerName.trim(),
                billingOwnerName: ownerName.trim(),
            });
            await continueFromSignup(result, authUser);
        } catch (signupError: unknown) {
            setError(getSaasErrorMessage(signupError));
            setIsSubmitting(false);
        }
    };

    /** Finish either immediate Free provisioning or the Stripe Checkout handoff. */
    const continueFromSignup = async (
        result: Awaited<ReturnType<typeof createSelfServeSignup>>,
        owner: User
    ): Promise<void> => {
        if (result.status === 'provisioned') {
            await prepareProvisionedDashboardSession(owner, result.providerId, draftId);
            reloadDashboard();
            return;
        }

        const checkout = result.checkoutUrl
            ? { checkoutUrl: result.checkoutUrl }
            : await createSubscriptionCheckoutSession(result.checkoutIntentId);
        if (!isSecureCheckoutUrl(checkout.checkoutUrl)) {
            throw new Error('Secure checkout could not be opened. Please try again.');
        }
        setPassword('');
        window.location.assign(checkout.checkoutUrl);
    };

    /** Upgrade the signed-in billing owner's existing Free trial clinic in place. */
    const upgradeExistingClinic = async () => {
        if (!selectedPlan || selectedPlan.planCode !== 'premium' || !interval || !subscriptionUnits ||
            !authUser || !isEmailVerified || eligibility?.accountStatus !== 'existing_self_service_owner' ||
            !eligibility.providerId || !eligibility.providerName) return;

        setError(null);
        setIsSubmitting(true);
        try {
            const result = await createSelfServeSignup({
                planCode: selectedPlan.planCode,
                versionId: selectedPlan.versionId,
                billingInterval: interval,
                subscriptionUnits,
                providerName: eligibility.providerName,
                billingOwnerName: authUser.displayName || undefined,
            });
            if (result.providerId !== eligibility.providerId) {
                throw new Error('The upgrade was returned for a different clinic. Please contact SuperPenguin support.');
            }
            await continueFromSignup(result, authUser);
        } catch (upgradeError: unknown) {
            setError(getSaasErrorMessage(upgradeError));
            setIsSubmitting(false);
        }
    };

    // Name checks improve feedback, while createSelfServeSignup remains the authoritative race-safe check.
    const checkClinicNameAvailability = async (name = providerName): Promise<boolean> => {
        const checkedName = name.trim();
        if (!checkedName || eligibility?.accountStatus !== 'eligible') {
            setClinicNameCheck({ name: checkedName, status: 'idle' });
            return false;
        }
        const requestNumber = ++clinicNameRequest.current;
        setClinicNameCheck({ name: checkedName, status: 'checking' });
        try {
            const result = await getSelfServeSignupEligibility(checkedName);
            if (requestNumber !== clinicNameRequest.current) return false;
            if (result.accountStatus !== 'eligible') {
                setEligibility(result);
                return false;
            }
            if (!result.clinicName) {
                throw new Error('Clinic name availability could not be verified. Please try again.');
            }
            const status = result.clinicName.available ? 'available' : 'taken';
            setClinicNameCheck({
                name: checkedName,
                status,
                message: result.clinicName.available
                    ? 'Clinic name is available.'
                    : 'That clinic name is already taken. Choose another clinic name.',
            });
            return result.clinicName.available;
        } catch (nameError: unknown) {
            if (requestNumber !== clinicNameRequest.current) return false;
            setClinicNameCheck({
                name: checkedName,
                status: 'error',
                message: getSaasErrorMessage(nameError, 'We could not check that clinic name. Please try again.'),
            });
            return false;
        }
    };

    const useAnotherAccount = async () => {
        await signOut(auth);
        clearSignupDraft(draftId);
        setDraftId(createSignupDraftId());
        pendingAccount.current = false;
        pendingAccountEmail.current = '';
        setHasPendingAccount(false);
        setAccountMode('create');
        setIsEmailVerified(false);
        setEmail('');
        setPassword('');
        setOwnerName('');
        setProviderName('');
        setError(null);
        setNotice(null);
        setEligibility(null);
        setEligibilityError(null);
        setClinicNameCheck({ name: '', status: 'idle' });
    };

    const openExistingDashboard = async () => {
        setError(null);
        setIsSubmitting(true);
        try {
            if (!authUser || !eligibility?.providerId) {
                throw new Error('Dashboard access could not be matched to a clinic. Please try again.');
            }
            await prepareProvisionedDashboardSession(authUser, eligibility.providerId, draftId);
            reloadDashboard();
        } catch (persistenceError: unknown) {
            setError(getSaasErrorMessage(persistenceError, 'We could not open the dashboard. Please try again.'));
            setIsSubmitting(false);
        }
    };

    if (isCatalogLoading || !isAuthReady) {
        return <PublicLayout compact><PublicLoading message='Preparing signup…' /></PublicLayout>;
    }
    if (catalogError) {
        return <PublicLayout compact><PublicError title='Signup is temporarily unavailable' message={catalogError} onRetry={reload} /></PublicLayout>;
    }
    const selectedUnitPrice = interval === 'monthly'
        ? selectedPlan?.monthlyDisplayPrice
        : selectedPlan?.annualDisplayPrice;
    const hasValidSubscriptionUnits = !!selectedPlan && !!subscriptionUnits &&
        Number.isInteger(subscriptionUnits) &&
        subscriptionUnits >= selectedPlan.minimumUnits &&
        subscriptionUnits <= selectedPlan.maximumSelfServeUnits &&
        (selectedPlan.planCode !== 'starter' || subscriptionUnits === 1) &&
        (selectedPlan.planCode === 'starter' || selectedUnitPrice !== undefined);
    if (!planCode || !validPlanCodes.includes(planCode) || !versionId || !interval || !selectedPlan ||
        !hasValidSubscriptionUnits) {
        return (
            <PublicLayout compact>
                <PublicError
                    title='Choose a current plan first'
                    message='This plan selection is missing or no longer published. Return to plans to make a current selection.'
                />
            </PublicLayout>
        );
    }

    const selectedPrice = getPremiumTotalPrice(selectedUnitPrice, subscriptionUnits!);
    const selectedDashboardSeats = selectedPlan.dashboardSeatsPerUnit * subscriptionUnits!;
    const selectedFamilyLicenses = selectedPlan.familyLicensesPerUnit * subscriptionUnits!;
    const selectedPlanName = selectedPlan.planCode === 'starter' ? 'Free trial' : selectedPlan.name;
    const canOpenExistingDashboard = eligibility?.accountStatus === 'existing_self_service_owner' ||
        (eligibility?.accountStatus === 'existing_dashboard_provider' && !!eligibility.providerId);
    const hasUnlinkedDashboardEmail = eligibility?.accountStatus === 'existing_dashboard_provider' &&
        !eligibility.providerId;
    // Older deployed Functions omit currentPlanCode, so the server remains the
    // authority until the extended eligibility response is deployed.
    const canUpgradeExistingClinic = selectedPlan.planCode === 'premium' &&
        eligibility?.accountStatus === 'existing_self_service_owner' &&
        !!eligibility.providerId && !!eligibility.providerName &&
        eligibility.currentPlanCode !== 'premium';

    return (
        <PublicLayout compact>
            <main className='saas-form-shell'>
                <section className='saas-form-intro'>
                    <Link to='/plans' className='saas-back-link'>
                        <ArrowBackRoundedIcon fontSize='inherit' aria-hidden='true' />
                        <span>Back to plans</span>
                    </Link>
                    <span className='saas-eyebrow'>{canUpgradeExistingClinic ? 'Upgrade your clinic' : 'Create your clinic'}</span>
                    <h1>{canUpgradeExistingClinic ? `Upgrade to ${selectedPlanName}` : `Start with ${selectedPlanName}`}</h1>
                    <p>{selectedPlan.description}</p>
                    <div className='saas-selection-summary saas-selection-summary--four'>
                        <div><span>Billing</span><strong>{selectedPlan.planCode === 'starter' ? 'Free' : interval === 'monthly' ? 'Monthly' : 'Annual'}</strong></div>
                        <div><span>Dashboard seats</span><strong>{selectedDashboardSeats}</strong></div>
                        <div><span>Family licences</span><strong>{selectedFamilyLicenses} / year</strong></div>
                        <div>
                            <span>Total</span>
                            <strong>{selectedPlan.planCode === 'starter'
                                ? 'Free'
                                : selectedPrice === undefined
                                    ? 'Unavailable'
                                    : `${gbpFormatter.format(selectedPrice)} + VAT`}</strong>
                        </div>
                    </div>
                    <p className='saas-fine-print'>Premium prices exclude VAT and continue in secure Stripe Checkout. SuperPenguin does not collect card details on this page.</p>
                </section>

                <section className='saas-form-card' aria-labelledby='signup-form-heading'>
                    {!authUser ? (
                        <form onSubmit={accountMode === 'create' ? createAccount : signInOwnerAccount}>
                            <h2 id='signup-form-heading'>{accountMode === 'create' ? 'Create your owner account' : 'Sign in to your owner account'}</h2>
                            {accountMode === 'create' ? (
                                <>
                                    <p>Already created an account for this signup? <button className='saas-inline-button' type='button' onClick={() => { setAccountMode('sign_in'); setError(null); }}>Sign in to continue</button></p>
                                    <label>Full name<input required autoComplete='name' value={ownerName} onChange={(event) => setOwnerName(event.target.value)} /></label>
                                    <label>Clinic name<input required autoComplete='organization' value={providerName} onChange={(event) => setProviderName(event.target.value)} /></label>
                                </>
                            ) : (
                                <p>
                                    {shouldResumeSignup
                                        ? 'Your email is verified. Sign in with the owner account you created to resume setting up your clinic. '
                                        : 'Use the owner account you created for this plan. '}
                                    <button
                                        className='saas-inline-button'
                                        type='button'
                                        onClick={() => {
                                            clearSignupDraft(draftId);
                                            setDraftId(createSignupDraftId());
                                            pendingAccount.current = false;
                                            pendingAccountEmail.current = '';
                                            setHasPendingAccount(false);
                                            setAccountMode('create');
                                            setError(null);
                                            setNotice(null);
                                        }}
                                    >
                                        Create a different account
                                    </button>
                                </p>
                            )}
                            <label>Work email<input required type='email' autoComplete='email' value={email} onChange={(event) => setEmail(event.target.value)} /></label>
                            <label>Password<input required minLength={8} type='password' autoComplete={accountMode === 'create' ? 'new-password' : 'current-password'} value={password} onChange={(event) => setPassword(event.target.value)} /></label>
                            {error && <p className='saas-inline-alert saas-inline-alert--error' role='alert'>{error}</p>}
                            {notice && <p className='saas-inline-alert' role='status'>{notice}</p>}
                            <button className='saas-button saas-button--wide' type='submit' disabled={isSubmitting}>
                                {isSubmitting ? (accountMode === 'create' ? 'Creating account…' : 'Signing in…') : (accountMode === 'create' ? 'Create account' : 'Sign in')}
                            </button>
                            <p className='saas-terms'>By continuing, you agree to SuperPenguin’s applicable service and privacy terms.</p>
                        </form>
                    ) : !isEmailVerified ? (
                        <div>
                            <span className='saas-status-icon' aria-hidden='true'>
                                <MailOutlineRoundedIcon fontSize='inherit' />
                            </span>
                            <h2 id='signup-form-heading'>Verify your email</h2>
                            <p>Open the verification link sent to <strong>{authUser.email}</strong>. Keep this page open, then confirm below.</p>
                            {error && <p className='saas-inline-alert saas-inline-alert--error' role='alert'>{error}</p>}
                            {notice && <p className='saas-inline-alert' role='status'>{notice}</p>}
                            <button className='saas-button saas-button--wide' type='button' onClick={refreshVerification} disabled={isSubmitting}>I have verified my email</button>
                            <button className='saas-secondary-button saas-button--wide' type='button' onClick={resendVerification} disabled={isSubmitting}>Resend verification email</button>
                            <button className='saas-link-button' type='button' onClick={useAnotherAccount}>Use a different account</button>
                        </div>
                    ) : isCheckingEligibility ? (
                        <div aria-busy='true'>
                            <h2 id='signup-form-heading'>Checking your account</h2>
                            <p role='status'>Confirming that <strong>{authUser.email}</strong> can create a new clinic…</p>
                        </div>
                    ) : eligibilityError || !eligibility ? (
                        <div>
                            <h2 id='signup-form-heading'>We could not check your account</h2>
                            <p>Signup is paused until we can safely confirm that this account is new.</p>
                            <p className='saas-inline-alert saas-inline-alert--error' role='alert'>{eligibilityError || 'Please try again.'}</p>
                            <button className='saas-button saas-button--wide' type='button' onClick={() => setEligibilityRefresh((value) => value + 1)}>Try again</button>
                            <button className='saas-link-button' type='button' onClick={useAnotherAccount}>Use another account</button>
                        </div>
                    ) : canUpgradeExistingClinic ? (
                        <div>
                            <h2 id='signup-form-heading'>Upgrade {eligibility.providerName} to Premium</h2>
                            <p>
                                Keep your existing clinic, users and family records. Your selected Premium capacity will
                                be applied after payment succeeds in secure Stripe Checkout.
                            </p>
                            <p>
                                <strong>{selectedDashboardSeats}</strong> dashboard seat{selectedDashboardSeats === 1 ? '' : 's'} and{' '}
                                <strong>{selectedFamilyLicenses}</strong> family licences per year.
                            </p>
                            {error && <p className='saas-inline-alert saas-inline-alert--error' role='alert'>{error}</p>}
                            <button
                                className='saas-button saas-button--wide'
                                type='button'
                                onClick={() => { void upgradeExistingClinic(); }}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Opening secure checkout…' : 'Continue to secure checkout'}
                            </button>
                            <button
                                className='saas-secondary-button saas-button--wide'
                                type='button'
                                onClick={openExistingDashboard}
                                disabled={isSubmitting}
                            >Back to dashboard</button>
                        </div>
                    ) : eligibility.accountStatus !== 'eligible' ? (
                        <div>
                            <h2 id='signup-form-heading'>
                                {eligibility.accountStatus === 'existing_self_service_owner'
                                    ? 'You already have a clinic'
                                    : hasUnlinkedDashboardEmail
                                        ? 'This email is linked to another dashboard account'
                                        : eligibility.accountStatus === 'existing_dashboard_provider'
                                        ? 'This account already has dashboard access'
                                        : 'This account needs support'}
                            </h2>
                            <p>
                                <strong>{authUser.email}</strong>{' '}
                                {eligibility.accountStatus === 'existing_self_service_owner'
                                    ? `already owns ${eligibility.providerName || 'a SuperPenguin clinic'}. Open the dashboard to manage its plan and billing.`
                                    : hasUnlinkedDashboardEmail
                                        ? 'matches an existing dashboard identity, but the account you just signed in with does not have dashboard access. Your new clinic has not been created. Try the check again, sign in with the original account, or contact SuperPenguin support.'
                                    : eligibility.accountStatus === 'existing_dashboard_provider'
                                        ? 'is already linked to a dashboard provider. Open the existing dashboard instead of creating another clinic.'
                                        : 'has dashboard records that could not be safely classified. Contact SuperPenguin support before continuing.'}
                            </p>
                            {error && <p className='saas-inline-alert saas-inline-alert--error' role='alert'>{error}</p>}
                            {canOpenExistingDashboard ? (
                                <button className='saas-button saas-button--wide' type='button' onClick={openExistingDashboard} disabled={isSubmitting}>Open existing dashboard</button>
                            ) : (
                                <button className='saas-button saas-button--wide' type='button' onClick={() => setEligibilityRefresh((value) => value + 1)} disabled={isCheckingEligibility}>Check again</button>
                            )}
                            <button className='saas-secondary-button saas-button--wide' type='button' onClick={useAnotherAccount}>Use another account</button>
                        </div>
                    ) : (
                        <form onSubmit={createClinic}>
                            <h2 id='signup-form-heading'>Confirm your clinic</h2>
                            <p>This clinic will belong to the signed-in account below. <button className='saas-inline-button' type='button' onClick={useAnotherAccount}>Use another account</button></p>
                            <label>Account email<input readOnly type='email' autoComplete='email' value={authUser.email || ''} /></label>
                            <label>Account owner name<input required autoComplete='name' value={ownerName} onChange={(event) => setOwnerName(event.target.value)} /></label>
                            <label>
                                Clinic name
                                <input
                                    required
                                    autoComplete='organization'
                                    aria-describedby='clinic-name-status'
                                    aria-invalid={clinicNameCheck.status === 'taken' || clinicNameCheck.status === 'error'}
                                    value={providerName}
                                    onChange={(event) => {
                                        const nextName = event.target.value;
                                        setProviderName(nextName);
                                        if (nextName.trim() !== clinicNameCheck.name) {
                                            clinicNameRequest.current += 1;
                                            setClinicNameCheck({ name: '', status: 'idle' });
                                        }
                                    }}
                                    onBlur={() => { void checkClinicNameAvailability(); }}
                                />
                            </label>
                            <span
                                id='clinic-name-status'
                                className={`saas-field-status saas-field-status--${clinicNameCheck.status}`}
                                role={clinicNameCheck.status === 'error' || clinicNameCheck.status === 'taken' ? 'alert' : 'status'}
                                aria-live='polite'
                            >
                                {clinicNameCheck.status === 'checking' ? 'Checking clinic name…' : clinicNameCheck.message || ''}
                            </span>
                            {error && <p className='saas-inline-alert saas-inline-alert--error' role='alert'>{error}</p>}
                            {notice && <p className='saas-inline-alert' role='status'>{notice}</p>}
                            <button className='saas-button saas-button--wide' type='submit' disabled={isSubmitting || clinicNameCheck.status === 'checking' || clinicNameCheck.status === 'taken'}>
                                {isSubmitting ? 'Creating your clinic…' : selectedPlan.planCode === 'starter' ? 'Create clinic' : 'Continue to secure checkout'}
                            </button>
                            <p className='saas-terms'>Your published plan version and price are verified again by the server before checkout.</p>
                        </form>
                    )}
                </section>
            </main>
        </PublicLayout>
    );
};
